import React, { useState, useMemo, useEffect, useRef } from "react";
import * as d3 from "d3";
import { useTheme } from "styled-components";
import { Container, MainContent } from "./styles";
import EmptyState from "../../components/EmptyState";
import ClientDetail from "./client";
import ClientsLoadingSkeleton from "./ClientsLoadingSkeleton";
import ClientsStatsLoadingSkeleton from "./ClientsStatsLoadingSkeleton";
import ClientsReformaLoadingSkeleton from "./ClientsReformaLoadingSkeleton";
import {
  GoPeople,
  GoZap,
  GoGraph,
  GoCalendar,
  GoSearch,
  GoStack,
  GoArrowUp,
  GoArrowDown,
  GoLaw,
  GoDatabase,
  GoCreditCard,
  GoOrganization,
  GoLocation,
  GoGlobe,
  GoPackage,
  GoAlert,
} from "react-icons/go";
import { BiSpreadsheet } from "react-icons/bi";
import { HiOutlineChartBar, HiOutlineSearch } from "react-icons/hi";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import api from "../../services/api";
import apiSped from "../../services/apiSped";
import { FiLoader } from "react-icons/fi";
import EntityListRow, { getTagColor } from "../../components/EntityListRow";
import usePersistedState from "../../hooks/usePersistedState";

// ==================== CRONOGRAMA REFORMA TRIBUTÁRIA ====================
// Durante a transição, os impostos antigos diminuem e os novos aumentam
const CRONOGRAMA_REFORMA = {
  2026: { cbs: 0.009, ibs: 0.001, reducaoAntigos: 0.00, label: "Início" },
  2027: { cbs: 0.088, ibs: 0.001, reducaoAntigos: 0.00, label: "Início" },
  2028: { cbs: 0.088, ibs: 0.001, reducaoAntigos: 0.00, label: "Trans." },
  2029: { cbs: 0.088, ibs: 0.023, reducaoAntigos: 0.10, label: "40%" },
  2030: { cbs: 0.088, ibs: 0.046, reducaoAntigos: 0.20, label: "60%" },
  2031: { cbs: 0.088, ibs: 0.069, reducaoAntigos: 0.30, label: "80%" },
  2032: { cbs: 0.088, ibs: 0.092, reducaoAntigos: 0.40, label: "Pleno" },
  2033: { cbs: 0.088, ibs: 0.177, reducaoAntigos: 1.00, label: "100%" },
};

// Dados mockados de clientes
const mockClientsData = [
  {
    id: 1,
    codigo: "PROD-001",
    nome: "Produto Alpha Premium",
    cnpj: "12.345.678/0001-90",
    segmento: "Tecnologia",
    tipoPessoa: "PJ",
    cidade: "São Paulo",
    uf: "SP",
    faturamento: 111000000,
    icms: 0,
    pisCofins: 125000,
    ibs: 87500,
    cbs: 0,
    is: 0,
  },
  {
    id: 2,
    codigo: "PROD-002",
    nome: "Beta Solution Pro",
    cnpj: "23.456.789/0001-01",
    segmento: "Indústria",
    tipoPessoa: "PJ",
    cidade: "Campinas",
    uf: "SP",
    faturamento: 81000000,
    icms: 0,
    pisCofins: 240000,
    ibs: 168000,
    cbs: 0,
    is: 0,
  },
  {
    id: 3,
    codigo: "PROD-003",
    nome: "Gamma Master Kit",
    cnpj: "123.456.789-00",
    segmento: "Comércio",
    tipoPessoa: "PF",
    cidade: "Rio de Janeiro",
    uf: "RJ",
    faturamento: 58800000,
    icms: 0,
    pisCofins: 60000,
    ibs: 42000,
    cbs: 0,
    is: 0,
  },
  {
    id: 4,
    nome: "Agropecuária Rural",
    cnpj: "45.678.901/0001-23",
    segmento: "Agronegócio",
    tipoPessoa: "PJ",
    cidade: "Ribeirão Preto",
    uf: "SP",
    faturamento: 3500000,
    icms: 0,
    pisCofins: 175000,
    ibs: 122500,
    cbs: 0,
    is: 0,
  },
  {
    id: 5,
    nome: "Construtora Delta",
    cnpj: "56.789.012/0001-34",
    segmento: "Construção",
    tipoPessoa: "PJ",
    cidade: "Belo Horizonte",
    uf: "MG",
    faturamento: 6200000,
    icms: 0,
    pisCofins: 310000,
    ibs: 217000,
    cbs: 0,
    is: 0,
  },
  {
    id: 6,
    nome: "Maria Santos",
    cnpj: "987.654.321-00",
    segmento: "Comércio",
    tipoPessoa: "PF",
    cidade: "Curitiba",
    uf: "PR",
    faturamento: 1800000,
    icms: 0,
    pisCofins: 90000,
    ibs: 63000,
    cbs: 0,
    is: 0,
  },
  {
    id: 7,
    nome: "Consultoria Empresarial",
    cnpj: "78.901.234/0001-56",
    segmento: "Serviços",
    tipoPessoa: "PJ",
    cidade: "São Paulo",
    uf: "SP",
    faturamento: 950000,
    icms: 0,
    pisCofins: 47500,
    ibs: 33250,
    cbs: 0,
    is: 0,
  },
  {
    id: 8,
    nome: "Carlos Oliveira",
    cnpj: "456.789.123-00",
    segmento: "Logística",
    tipoPessoa: "PF",
    cidade: "Porto Alegre",
    uf: "RS",
    faturamento: 2100000,
    icms: 0,
    pisCofins: 105000,
    ibs: 73500,
    cbs: 0,
    is: 0,
  },
  {
    id: 9,
    nome: "Farmácia Popular",
    cnpj: "90.123.456/0001-78",
    segmento: "Saúde",
    tipoPessoa: "PJ",
    cidade: "Salvador",
    uf: "BA",
    faturamento: 780000,
    icms: 0,
    pisCofins: 39000,
    ibs: 27300,
    cbs: 0,
    is: 0,
  },
  {
    id: 10,
    nome: "Ana Pereira",
    cnpj: "321.654.987-00",
    segmento: "Alimentação",
    tipoPessoa: "PF",
    cidade: "Florianópolis",
    uf: "SC",
    faturamento: 520000,
    icms: 0,
    pisCofins: 26000,
    ibs: 18200,
    cbs: 0,
    is: 0,
  },
];

// Função para gerar cores de clientes
const getClientColor = (cnpj) => {
  const colors = [
    "#3b82f6",
    "#10b981",
    "#10b981",
    "#ef4444",
    "#8b5cf6",
    "#06b6d4",
    "#ec4899",
    "#84cc16",
    "#f97316",
    "#6366f1",
  ];
  const hash =
    cnpj?.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0) || 0;
  return colors[hash % colors.length];
};

// Função para formatar números
const formatNumber = (num) => {
  if (!num) return "R$ 0,00";
  return `R$ ${num.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

// Função para formatar números de forma compacta (K, M)
const formatCompact = (num) => {
  if (!num || num === 0) return "R$ 0";
  
  const absNum = Math.abs(num);
  
  if (absNum >= 1000000) {
    return `R$ ${(num / 1000000).toFixed(1)}M`;
  } else if (absNum >= 1000) {
    return `R$ ${(num / 1000).toFixed(1)}K`;
  }
  
  return `R$ ${num.toFixed(0)}`;
};

// Componente do Mapa do Brasil usando D3.js
const BrazilMapCard = ({ data, formatNumber, geoStats }) => {
  const svgRef = useRef();
  const [geoData, setGeoData] = useState(null);
  const [hoveredState, setHoveredState] = useState(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  // Mapeamento de UF para nome do estado
  const nomeEstados = {
    AC: "Acre",
    AL: "Alagoas",
    AP: "Amapá",
    AM: "Amazonas",
    BA: "Bahia",
    CE: "Ceará",
    DF: "Distrito Federal",
    ES: "Espírito Santo",
    GO: "Goiás",
    MA: "Maranhão",
    MT: "Mato Grosso",
    MS: "Mato Grosso do Sul",
    MG: "Minas Gerais",
    PA: "Pará",
    PB: "Paraíba",
    PR: "Paraná",
    PE: "Pernambuco",
    PI: "Piauí",
    RJ: "Rio de Janeiro",
    RN: "Rio Grande do Norte",
    RS: "Rio Grande do Sul",
    RO: "Rondônia",
    RR: "Roraima",
    SC: "Santa Catarina",
    SP: "São Paulo",
    SE: "Sergipe",
    TO: "Tocantins",
  };

  // Processar dados por UF
  const ufData = useMemo(() => {
    if (data && data.length > 0) { // Fallback para lista de clientes
      const ufMap = new Map();
      data.forEach((client) => {
        const uf = client.uf;
        if (!ufMap.has(uf)) ufMap.set(uf, { uf, faturamento: 0, count: 0 });
        const d = ufMap.get(uf);
        d.faturamento += client.faturamento || 0;
        d.count += 1;
      });
      return ufMap;
    }
    return new Map();
  }, [data]);

  const mapData = useMemo(() => {
     if (geoStats) {
        const map = new Map();
        geoStats.forEach(d => {
           map.set(d.id, { uf: d.id, faturamento: d.faturamento, count: d.count });
        });
        return map;
     }
     return ufData;
  }, [geoStats, ufData]);

  const totalFat = useMemo(() => {
    return Array.from(mapData.values()).reduce(
      (sum, d) => sum + d.faturamento,
      0
    );
  }, [mapData]);

  // Carregar GeoJSON do Brasil
  useEffect(() => {
    fetch(
      "https://raw.githubusercontent.com/codeforamerica/click_that_hood/master/public/data/brazil-states.geojson"
    )
      .then((res) => res.json())
      .then((data) => setGeoData(data))
      .catch((err) => console.error("Erro ao carregar GeoJSON:", err));
  }, []);

  // Renderizar mapa com D3
  useEffect(() => {
    if (!geoData || !svgRef.current) return;

    const svg = d3.select(svgRef.current);
    const width = 280;
    const height = 280;

    svg.selectAll("*").remove();

    const projection = d3
      .geoMercator()
      .center([-52, -15])
      .scale(380)
      .translate([width / 2, height / 2]);

    const path = d3.geoPath().projection(projection);

    // Escala de cores dinâmica
    const maxFaturamento = Math.max(
      ...Array.from(mapData.values()).map((d) => d.faturamento),
      1
    );

    // Usando escala logarítmica para melhor distribuição visual entre estados díspares
    const colorScale = d3
      .scaleSequential(d3.interpolateBlues)
      .domain([0, Math.log1p(maxFaturamento)]);

    const getColor = (uf) => {
      const stateData = mapData.get(uf);
      if (!stateData || stateData.faturamento === 0)
        return "rgba(55, 65, 81, 0.5)"; // Cinza escuro para sem dados
      
      // Aplicar log para suavizar as diferenças extremas
      return colorScale(Math.log1p(stateData.faturamento));
    };

    // Desenhar estados
    svg
      .selectAll("path")
      .data(geoData.features)
      .enter()
      .append("path")
      .attr("d", path)
      .attr("fill", (d) => {
        const uf =
          d.properties.sigla ||
          d.properties.name?.substring(0, 2)?.toUpperCase();
        return getColor(uf);
      })
      .attr("stroke", "rgba(255, 255, 255, 0.3)")
      .attr("stroke-width", 0.5)
      .attr("cursor", "pointer")
      .style("opacity", 0.8)
      .style("transition", "all 0.2s ease")
      .on("mouseover", function (event, d) {
        const uf =
          d.properties.sigla ||
          d.properties.name?.substring(0, 2)?.toUpperCase();
        const stateData = mapData.get(uf) || { uf, faturamento: 0, count: 0 };
        d3.select(this)
          .attr("stroke", "#fff")
          .attr("stroke-width", 1.5)
          .style("opacity", 1);
        setHoveredState({ ...stateData, nome: nomeEstados[uf] || uf });
        setMousePos({ x: event.pageX, y: event.pageY });
      })
      .on("mousemove", (event) =>
        setMousePos({ x: event.pageX, y: event.pageY })
      )
      .on("mouseout", function () {
        d3.select(this)
          .attr("stroke", "rgba(255, 255, 255, 0.3)")
          .attr("stroke-width", 0.5)
          .style("opacity", 0.8);
        setHoveredState(null);
      });

    // Labels dos estados
    svg
      .selectAll("text")
      .data(geoData.features)
      .enter()
      .append("text")
      .attr("x", (d) => path.centroid(d)[0])
      .attr("y", (d) => path.centroid(d)[1])
      .attr("text-anchor", "middle")
      .attr("dy", "0.35em")
      .attr("font-size", "7px")
      .attr("font-weight", "600")
      .attr("fill", "#fff")
      .attr("pointer-events", "none")
      .style("text-shadow", "1px 1px 2px rgba(0,0,0,0.8)")
      .text(
        (d) =>
          d.properties.sigla ||
          d.properties.name?.substring(0, 2)?.toUpperCase()
      );
  }, [geoData, mapData]);

  return (
    <div className="stats-section chart-card">
      <div className="section-header">
        <GoGlobe />
        <h4>Faturamento por Estado</h4>
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: "10px 0",
        }}
      >
        <svg
          ref={svgRef}
          width="280"
          height="280"
          viewBox="0 0 280 280"
          style={{ maxWidth: "100%" }}
        />
        {/* Legenda */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "8px",
            justifyContent: "center",
            marginTop: "12px",
          }}
        >
          {[
            { color: "#1d4ed8", label: "Alto" },
            { color: "#60a5fa", label: "Médio" },
            { color: "#bfdbfe", label: "Baixo" },
            { color: "rgba(55, 65, 81, 0.5)", label: "Sem dados" },
          ].map((item) => (
            <div
              key={item.label}
              style={{ display: "flex", alignItems: "center", gap: "4px" }}
            >
              <div
                style={{
                  width: "12px",
                  height: "12px",
                  borderRadius: "2px",
                  background: item.color,
                }}
              ></div>
              <span style={{ fontSize: "0.65em", color: "#94a3b8" }}>
                {item.label}
              </span>
            </div>
          ))}
        </div>
      </div>
      {/* Tooltip */}
      {hoveredState && (
        <div
          style={{
            position: "fixed",
            left: mousePos.x + 15,
            top: mousePos.y + 15,
            zIndex: 999999,
            background: "rgba(0, 0, 0, 0.9)",
            color: "white",
            padding: "12px 16px",
            borderRadius: "8px",
            fontSize: "13px",
            pointerEvents: "none",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.6)",
            minWidth: "200px",
            border: "1px solid rgba(255, 255, 255, 0.2)",
          }}
        >
          <div
            style={{
              fontWeight: 600,
              fontSize: "14px",
              marginBottom: "8px",
              borderBottom: "1px solid rgba(255,255,255,0.2)",
              paddingBottom: "6px",
            }}
          >
            {hoveredState.uf} - {hoveredState.nome}
          </div>
          <div
            style={{
              color: "#10b981",
              fontWeight: 700,
              fontSize: "16px",
              marginBottom: "6px",
            }}
          >
            {formatNumber(hoveredState.faturamento)}
          </div>
          <div
            style={{ color: "#60a5fa", fontSize: "12px", marginBottom: "4px" }}
          >
            {totalFat > 0
              ? ((hoveredState.faturamento / totalFat) * 100).toFixed(1)
              : 0}
            % do total
          </div>
          <div style={{ color: "#94a3b8", fontSize: "11px" }}>
            {hoveredState.count} cliente{hoveredState.count !== 1 ? "s" : ""}
          </div>
        </div>
      )}
    </div>
  );
};

export default function Clients() {
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedClient, setSelectedClient] = useState(null);
  // Tab ativa persistida
  const [activeTab, setActiveTab] = usePersistedState(
    "vidal_clients_tab",
    "geral"
  );
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  
  // Estado para o mês selecionado no hover
  const [selectedMonth, setSelectedMonth] = useState({ 
     mesNome: "", mesIndex: 0, novos: 0, ativos: 0, faturamento: 0 
  });

  // Reforma Tributária States
  const [reformaData, setReformaData] = useState(null);
  const [reformaLoading, setReformaLoading] = useState(false);
  const [reformaError, setReformaError] = useState(null);
  const [selectedCenario, setSelectedCenario] = useState("2027");
  const [reformaSearchTerm, setReformaSearchTerm] = useState("");
  const [reformaSearchResults, setReformaSearchResults] = useState([]);

  // API pagination states for geral tab
  const [clientsData, setClientsData] = useState([]);
  const [clientsPage, setClientsPage] = useState(1);
  const [clientsHasMore, setClientsHasMore] = useState(true);
  const [clientsIsLoadingMore, setClientsIsLoadingMore] = useState(false);
  const clientsObserverTarget = useRef(null);

  // API pagination states for reforma tab
  const [reformaClients, setReformaClients] = useState([]);
  const [reformaPage, setReformaPage] = useState(1);
  const [reformaHasMore, setReformaHasMore] = useState(true);
  const [reformaIsLoadingMore, setReformaIsLoadingMore] = useState(false);
  const reformaObserverTarget = useRef(null);

  // Search state for visual feedback
  const [isSearching, setIsSearching] = useState(false);

  // Buscar dados do dashboard (KPIs, gráficos, etc)
  useEffect(() => {
    const fetchDashboard = async () => {
       try {
         setLoading(true);
         const projectId = localStorage.getItem("vidal_selected_project");
         if (!projectId) return;

         const response = await api.get(`/clients/${projectId}/dashboard`);
         if (response.data.success) {
            const data = response.data.data;
            setDashboardData(data);
            if (data.evolucao && data.evolucao.length > 0) {
               setSelectedMonth(data.evolucao[data.evolucao.length - 1]);
            }
         }
       } catch (error) {
         console.error("Erro ao carregar dashboard clientes:", error);
       } finally {
         setLoading(false);
       }
    };
    fetchDashboard();
  }, []);

  // Buscar lista paginada de clientes para aba geral
  const fetchClients = async (page = 1, search = "", append = false) => {
    try {
      if (!append) setLoading(true);
      setClientsIsLoadingMore(true);
      
      const projectId = localStorage.getItem("vidal_selected_project");
      const response = await api.get(`/clients/${projectId}/list`, {
        params: { page, limit: 50, search }
      });
      
      if (response.data.success) {
        const newClients = response.data.data.clientes || [];
        if (append) {
          setClientsData(prev => [...prev, ...newClients]);
        } else {
          setClientsData(newClients);
        }
        setClientsPage(page);
        setClientsHasMore(response.data.data.pagination.hasMore);
      }
    } catch (err) {
      console.error('Erro ao buscar clientes paginados:', err);
    } finally {
      setLoading(false);
      setClientsIsLoadingMore(false);
    }
  };

  // Carregar clientes na montagem do componente
  const hasLoadedClients = useRef(false);
  
  useEffect(() => {
    if (!hasLoadedClients.current) {
      hasLoadedClients.current = true;
      fetchClients(1, searchTerm, false);
    }
  }, []);

  // Recarregar clientes quando busca mudar
  useEffect(() => {
    if (hasLoadedClients.current) {
      const timeoutId = setTimeout(() => {
        fetchClients(1, searchTerm, false);
      }, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [searchTerm]);

  // Dados mensais para o gráfico
  const monthlyChartData = useMemo(() => {
     return dashboardData?.evolucao || [];
  }, [dashboardData]);

  // Calcular totais
  const totals = useMemo(() => {
     return dashboardData?.kpis || { clientes: 0, faturamento: 0, impostos: 0 };
  }, [dashboardData]);

  // Calcular impostos detalhados
  const taxTotals = useMemo(() => {
     return dashboardData?.impostosDetalhados || { icms: 0, pisCofins: 0, ibs: 0, cbs: 0, is: 0 };
  }, [dashboardData]);

  // Processar tipos de pessoa (PF/PJ)
  const tipoPessoaData = useMemo(() => {
     const tipos = dashboardData?.tipoPessoa || [];
     const totalFaturamento = tipos.reduce((acc, t) => acc + (t.faturamento || 0), 0);
     const totalCount = tipos.reduce((acc, t) => acc + (t.count || 0), 0);
     
     return tipos.map(t => ({
        ...t,
        percentage: totalFaturamento > 0 ? ((t.faturamento / totalFaturamento) * 100).toFixed(1) : 0,
        countPercentage: totalCount > 0 ? ((t.count / totalCount) * 100).toFixed(1) : 0,
        count: t.count || 0,
        faturamento: t.faturamento || 0
     })).sort((a,b) => b.faturamento - a.faturamento);
  }, [dashboardData]);

  const maxFaturamento = Math.max(...(tipoPessoaData.map((t) => t.faturamento) || [0]));

  // Infinite scroll observer for geral tab API pagination
  useEffect(() => {
    if (activeTab !== 'geral') return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && clientsHasMore && !clientsIsLoadingMore) {
          const nextPage = clientsPage + 1;
          fetchClients(nextPage, searchTerm, true);
        }
      },
      { threshold: 0.1 }
    );
    
    if (clientsObserverTarget.current) {
      observer.observe(clientsObserverTarget.current);
    }
    
    return () => {
      if (clientsObserverTarget.current) {
        observer.unobserve(clientsObserverTarget.current);
      }
    };
  }, [activeTab, clientsHasMore, clientsIsLoadingMore, clientsPage, searchTerm]);

  // Calcular impostos de um cliente
  const calculateTotalTaxes = (client) => {
    if (!client) return 0;
    return (client.icms || 0) + (client.pisCofins || 0) + (client.ibs || 0) + (client.cbs || 0) + (client.is || 0);
  };

  // Buscar dados da reforma quando a aba estiver ativa
  const fetchReformaData = async () => {
    try {
      setReformaLoading(true);
      setReformaError(null);
      const projectId = localStorage.getItem("vidal_selected_project");
      const response = await api.get('/reforma/clientes/dashboard', {
        headers: { projeto: projectId }
      });
      
      if (response.data) {
         setReformaData(response.data);
      }
    } catch (err) {
      console.error('Erro ao buscar dados da reforma:', err);
      // Fallback: create an empty structure to prevent crashes
      setReformaData({ resumo: {}, categorias: [] }); 
    } finally {
      setReformaLoading(false);
    }
  };

  // Buscar lista paginada de clientes para reforma
  const fetchReformaClients = async (page = 1, append = false) => {
    try {
      if (!append) setReformaLoading(true);
      setReformaIsLoadingMore(true);
      
      const projectId = localStorage.getItem("vidal_selected_project");
      const response = await api.get(`/clients/${projectId}/list`, {
        params: { page, limit: 50 }
      });
      
      if (response.data.success) {
        const newClients = response.data.data.clientes || [];
        if (append) {
          setReformaClients(prev => [...prev, ...newClients]);
        } else {
          setReformaClients(newClients);
        }
        setReformaPage(page);
        setReformaHasMore(response.data.data.pagination.hasMore);
      }
    } catch (err) {
      console.error('Erro ao buscar clientes paginados:', err);
    } finally {
      setReformaLoading(false);
      setReformaIsLoadingMore(false);
    }
  };

  // Buscar clientes via API quando houver termo de busca na aba Reforma
  useEffect(() => {
    if (activeTab !== 'reforma') return;
    
    const searchClients = async () => {
      if (!reformaSearchTerm.trim()) {
        setReformaSearchResults([]);
        return;
      }

      try {
        const projectId = localStorage.getItem("vidal_selected_project");
        const response = await api.get(`/clients/${projectId}/list`, {
          params: {
            page: 1,
            limit: 100,
            search: reformaSearchTerm,
          },
        });

        if (response.data.success) {
          setReformaSearchResults(response.data.data.clientes || []);
        }
      } catch (err) {
        console.error("Erro ao buscar clientes na reforma:", err);
      }
    };

    const timeoutId = setTimeout(searchClients, 300);
    return () => clearTimeout(timeoutId);
  }, [reformaSearchTerm, activeTab]);

  // Ref to track if reforma data was already loaded
  const hasLoadedReformaClients = useRef(false);
  
  useEffect(() => {
    if (activeTab === 'reforma' && !hasLoadedReformaClients.current) {
      hasLoadedReformaClients.current = true;
      fetchReformaData();
      fetchReformaClients(1);
    }
  }, [activeTab]);

  // Infinite scroll observer for reforma tab API pagination
  useEffect(() => {
    if (activeTab !== 'reforma') return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && reformaHasMore && !reformaIsLoadingMore) {
          const nextPage = reformaPage + 1;
          fetchReformaClients(nextPage, true);
        }
      },
      { threshold: 0.1 }
    );
    
    if (reformaObserverTarget.current) {
      observer.observe(reformaObserverTarget.current);
    }
    
    return () => {
      if (reformaObserverTarget.current) {
        observer.unobserve(reformaObserverTarget.current);
      }
    };
  }, [activeTab, reformaHasMore, reformaIsLoadingMore, reformaPage]);
  
  // Calcular KPIs da Reforma
  const reformaKpis = useMemo(() => {
    if (!reformaData?.resumo) {
       return {
         faturamentoTotal: 0,
         totalClientes: 0,
         totalSegmentos: 0,
         debitosAtuais: 0,
         ivaDual: 0,
         impacto: 0,
         variacaoPercentual: 0,
         aliquotaEffective: 0,
         aliquotaCBS: 0,
         aliquotaIBS: 0,
         icms: 0,
         pis: 0,
         cofins: 0,
         iss: 0,
         ipi: 0,
         cbs: 0,
         ibs: 0
       };
    }
    
    const resumo = reformaData.resumo;
    const faturamentoTotal = resumo.faturamentoTotal || resumo.valorMercadorias || 0;
    const totalClientes = resumo.totalClientes || resumo.totalProdutos || 0;
    const totalSegmentos = resumo.totalSegmentos || resumo.totalFornecedores || 0;

    // Alíquotas do cenário selecionado usando CRONOGRAMA_REFORMA
    const cenarioAno = parseInt(selectedCenario);
    const cenario = CRONOGRAMA_REFORMA[cenarioAno] || CRONOGRAMA_REFORMA["2027"];
    const aliquotaCBS = cenario.cbs;
    const aliquotaIBS = cenario.ibs;
    const reducaoAntigos = cenario.reducaoAntigos || 0;
    
    // Valores base dos impostos atuais (2024)
    const icmsBase = resumo.icmsBase || resumo.icms || 0;
    const pisBase = resumo.pisBase || resumo.pis || 0;
    const cofinsBase = resumo.cofinsBase || resumo.cofins || 0;
    const issBase = resumo.issBase || resumo.iss || 0;
    const ipiBase = resumo.ipiBase || resumo.ipi || 0;
    
    // Aplicar redução gradual dos impostos antigos
    const icms = icmsBase * (1 - reducaoAntigos);
    const pis = pisBase * (1 - reducaoAntigos);
    const cofins = cofinsBase * (1 - reducaoAntigos);
    const iss = issBase * (1 - reducaoAntigos);
    const ipi = ipiBase * (1 - reducaoAntigos);
    
    const debitosAtuais = icms + pis + cofins + iss + ipi;
    
    // Cálculo IVA Dual com alíquotas do cenário selecionado
    const baseCalculo = faturamentoTotal; 
    const cbsProjetado = baseCalculo * aliquotaCBS;
    const ibsProjetado = baseCalculo * aliquotaIBS;
    const totalIvaDual = cbsProjetado + ibsProjetado;
    
    const impacto = totalIvaDual - debitosAtuais;
    const variacaoPercentual = debitosAtuais ? (impacto / debitosAtuais) * 100 : 0;
    
    return {
        faturamentoTotal,
        totalClientes,
        totalSegmentos,
        debitosAtuais,
        ivaDual: totalIvaDual,
        cbs: cbsProjetado,
        ibs: ibsProjetado,
        aliquotaCBS: aliquotaCBS * 100,
        aliquotaIBS: aliquotaIBS * 100,
        icms, pis, cofins, iss, ipi,
        impacto,
        variacaoPercentual
    };
  }, [reformaData, selectedCenario]);

  const comparativoTributosData = useMemo(() => {
      // Durante a transição, os impostos antigos diminuem e os novos aumentam
      // Mostramos os valores atuais (com redução aplicada) vs os novos impostos
      
      // Obter informações do cenário atual
      const cenarioAno = parseInt(selectedCenario);
      const cenario = CRONOGRAMA_REFORMA[cenarioAno] || CRONOGRAMA_REFORMA["2027"];
      
      const data = [
          { name: "ICMS", atual: reformaKpis.icms, novo: 0 },
          { name: "PIS", atual: reformaKpis.pis, novo: 0 },
          { name: "COFINS", atual: reformaKpis.cofins, novo: 0 },
          { name: "ISS/IPI", atual: reformaKpis.iss + reformaKpis.ipi, novo: 0 },
          { name: "CBS", atual: 0, novo: reformaKpis.cbs },
          { name: "IBS", atual: 0, novo: reformaKpis.ibs },
      ];
      
      console.log('=== GRÁFICO COMPARATIVO ===');
      console.log('Cenário selecionado:', selectedCenario);
      console.log('Redução impostos antigos:', (cenario.reducaoAntigos * 100).toFixed(0) + '%');
      console.log('Alíquota CBS:', (cenario.cbs * 100).toFixed(2) + '%');
      console.log('Alíquota IBS:', (cenario.ibs * 100).toFixed(2) + '%');
      console.log('Dados do gráfico:', data);
      console.log('Total Antigos:', data.slice(0, 4).reduce((sum, d) => sum + d.atual, 0));
      console.log('Total Novos:', data.slice(4).reduce((sum, d) => sum + d.novo, 0));
      console.log('===========================');
      
      return data;
  }, [reformaKpis, selectedCenario]);

  // Se um cliente estiver selecionado, mostrar a tela de detalhe
  if (selectedClient) {
    return (
      <ClientDetail
        cliente={selectedClient}
        onVoltar={() => setSelectedClient(null)}
        initialTab={activeTab === "reforma" ? "reforma" : "geral"}
      />
    );
  }

  return (
    <Container>
      {/* Header */}
      <div className="header-info">
        <div className="header-details">
          <h3>Dashboard de Clientes</h3>
          <div className="header-meta">
            <span className="status-tag active">Sistema Online</span>
            <span className="update-tag">Atualizado agora</span>
          </div>
        </div>
      </div>

      {/* Tabs - Geral / Estatísticas / Reforma Tributária */}
      <div className="tabs-header">
        <button
          className={`tab ${activeTab === "geral" ? "active" : ""}`}
          onClick={() => setActiveTab("geral")}
        >
          <BiSpreadsheet />
          Geral
        </button>
        <button
          className={`tab ${activeTab === "estatisticas" ? "active" : ""}`}
          onClick={() => setActiveTab("estatisticas")}
        >
          <HiOutlineChartBar />
          Estatísticas
        </button>
        <button
          className={`tab ${activeTab === "reforma" ? "active" : ""}`}
          onClick={() => setActiveTab("reforma")}
        >
          <GoLaw />
          Reforma Tributária
        </button>
      </div>

      {/* Empty State — nenhum SPED carregado */}
      {!loading && clientsData.length === 0 && (
        <EmptyState screen="clients" />
      )}

      {/* Loading State */}
      {loading && activeTab === "geral" && (
        <ClientsLoadingSkeleton isDark={theme.body !== "#fafbfc"} />
      )}
      
      {loading && activeTab === "estatisticas" && (
        <ClientsStatsLoadingSkeleton isDark={theme.body !== "#fafbfc"} />
      )}
      
      {loading && activeTab === "reforma" && (
        <ClientsReformaLoadingSkeleton isDark={theme.body !== "#fafbfc"} />
      )}

      {/* ========== TAB GERAL ========== */}
      {activeTab === "geral" && !loading && clientsData.length > 0 && (
        <>
          {/* KPI Cards - 4 principais */}
          <div className="kpi-cards main-stats">
            <div className="kpi-card primary">
              <div className="card-content">
                <div className="thumb">
                  <GoPeople />
                </div>
                <div className="text">
                  <p>{totals.clientes}</p>
                  <p>Clientes Cadastrados</p>
                </div>
              </div>
            </div>

            <div className="kpi-card success">
              <div className="card-content">
                <div className="thumb">
                  <GoGraph />
                </div>
                <div className="text">
                  <p>{formatCompact(totals.faturamento)}</p>
                  <p>Faturamento Total</p>
                </div>
              </div>
            </div>



            <div className="kpi-card info">
              <div className="card-content">
                <div className="thumb">
                  <GoCalendar />
                </div>
                <div className="text">
                  <p>{formatCompact(totals.impostos)}</p>
                  <p>Total de Impostos</p>
                </div>
              </div>
            </div>
          </div>

          {/* KPI Cards - Impostos */}
          <div className="kpi-cards tax-stats">
            <div className="kpi-card compact tax-icms">
              <div className="card-content">
                <div className="thumb">
                  <GoCalendar />
                </div>
                <div className="text">
                  <p>{formatCompact(taxTotals.icms)}</p>
                  <p>ICMS</p>
                </div>
              </div>
            </div>

            <div className="kpi-card compact tax-piscofins">
              <div className="card-content">
                <div className="thumb">
                  <GoLaw />
                </div>
                <div className="text">
                  <p>{formatCompact(taxTotals.pisCofins)}</p>
                  <p>PIS/COFINS</p>
                </div>
              </div>
            </div>

            <div className="kpi-card compact tax-ipi">
              <div className="card-content">
                <div className="thumb">
                  <GoPackage />
                </div>
                <div className="text">
                  <p>{formatCompact(taxTotals.ipi || 0)}</p>
                  <p>IPI</p>
                </div>
              </div>
            </div>

            <div className="kpi-card compact tax-ibs">
              <div className="card-content">
                <div className="thumb">
                  <GoDatabase />
                </div>
                <div className="text">
                  <p>{formatCompact(taxTotals.ibs)}</p>
                  <p>IBS</p>
                </div>
              </div>
            </div>

            <div className="kpi-card compact tax-cbs">
              <div className="card-content">
                <div className="thumb">
                  <GoCreditCard />
                </div>
                <div className="text">
                  <p>{formatCompact(taxTotals.cbs)}</p>
                  <p>CBS</p>
                </div>
              </div>
            </div>

            <div className="kpi-card compact tax-is">
              <div className="card-content">
                <div className="thumb">
                  <GoGraph />
                </div>
                <div className="text">
                  <p>{formatCompact(taxTotals.is)}</p>
                  <p>IS</p>
                </div>
              </div>
            </div>
          </div>

          {/* Filtros */}
          <div className="filters">
            <div className="search-input-wrapper">
              <div className="search-icon">
                {isSearching ? (
                  <FiLoader className="spin" style={{ animation: 'spin 1s linear infinite' }} />
                ) : (
                  <GoSearch />
                )}
              </div>
              <input
                type="text"
                placeholder="Buscar por nome ou CNPJ do cliente..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: 0.6,
                    transition: 'opacity 0.2s',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                  onMouseLeave={(e) => e.currentTarget.style.opacity = '0.6'}
                  title="Limpar busca"
                >
                  <GoAlert style={{ fontSize: '16px', transform: 'rotate(45deg)' }} />
                </button>
              )}
            </div>
            {searchTerm && !isSearching && (
              <div style={{
                fontSize: '0.85em',
                opacity: 0.7,
                marginTop: '8px',
                marginLeft: '4px'
              }}>
                {clientsData.length === 0 ? (
                  <span style={{ color: '#ef4444' }}>Nenhum cliente encontrado</span>
                ) : (
                  <span>
                    {clientsData.length} cliente{clientsData.length !== 1 ? 's' : ''} encontrado{clientsData.length !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Conteúdo Principal */}
          <MainContent>
            {/* Lista de Clientes */}
            <div className="clients-table">
              {/* Header da Tabela */}
              <div className="table-header">
                <div className="header-cell cliente">Cliente</div>
                <div className="header-cell">Segmento</div>
                <div className="header-cell">UF</div>
                <div className="header-cell">Faturamento</div>
                <div className="header-cell">Impostos</div>
              </div>

              {/* Lista de Clientes */}
              <div className="table-body">
                {(clientsData || []).map((client, index) => (
                    <EntityListRow
                      key={`client-${index}`}
                      entity={client}
                      color={getClientColor(client.cnpj)}
                      icon={GoPeople}
                      eyebrow={client.cnpj}
                      title={client.nome}
                      tags={[
                        { label: client.segmento || "Outros", color: getTagColor(client.cnpj) },
                        { label: client.uf, neutral: true },
                      ]}
                      columns={[
                        { width: "minmax(0, 2fr)" },
                        { width: "minmax(0, 1fr)", render: (c) => c.segmento || "—" },
                        { width: "minmax(0, 1fr)", render: (c) => c.uf || "—" },
                        { width: "minmax(0, 1fr)", render: (c) => formatNumber(c.faturamento || 0) },
                        { width: "minmax(0, 1fr)", render: (c) => formatNumber(calculateTotalTaxes(c)) },
                      ]}
                      onClick={() => setSelectedClient(client)}
                    />
                ))}
                
                {/* Observer target for infinite scroll */}
                {clientsHasMore && (
                  <div ref={clientsObserverTarget} style={{ padding: '20px', textAlign: 'center' }}>
                    {clientsIsLoadingMore && (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                        <FiLoader style={{ animation: 'spin 1s linear infinite' }} />
                        <span style={{ fontSize: '0.85em', opacity: 0.7 }}>Carregando mais clientes...</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>


            {/* Card de Receita por Segmento */}
            <div className="segments-card">
              <div className="donut-wrapper">
                <div className="donut-header">
                  <h4>Segmentos de Clientes</h4>
                  <p>Participação percentual no faturamento</p>
                </div>
                <div className="donut-chart">
                  <svg width="180" height="180" viewBox="0 0 200 200">
                      {(() => {
                        const total = tipoPessoaData.reduce(
                          (sum, tipo) => sum + tipo.faturamento,
                          0
                        );
                        let currentAngle = 0;
                        const radius = 80;
                        const innerRadius = 50;
                        const centerX = 100;
                        const centerY = 100;

                        return tipoPessoaData.map((tipo, index) => {
                          if (total === 0) return null;
                          
                          const percentage = tipo.faturamento / total;
                          const angle = percentage * 360;
                          const startAngle = currentAngle;
                          const endAngle = currentAngle + angle;
                          const startAngleRad = (startAngle * Math.PI) / 180;
                          const endAngleRad = (endAngle * Math.PI) / 180;
                          const largeArcFlag = angle > 180 ? 1 : 0;

                          const x1 = centerX + radius * Math.cos(startAngleRad);
                          const y1 = centerY + radius * Math.sin(startAngleRad);
                          const x2 = centerX + radius * Math.cos(endAngleRad);
                          const y2 = centerY + radius * Math.sin(endAngleRad);
                          const ix1 =
                            centerX + innerRadius * Math.cos(startAngleRad);
                          const iy1 =
                            centerY + innerRadius * Math.sin(startAngleRad);
                          const ix2 = centerX + innerRadius * Math.cos(endAngleRad);
                          const iy2 = centerY + innerRadius * Math.sin(endAngleRad);

                          const pathData = [
                            `M ${ix1} ${iy1}`,
                            `L ${x1} ${y1}`,
                            `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
                            `L ${ix2} ${iy2}`,
                            `A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${ix1} ${iy1}`,
                            "Z",
                          ].join(" ");

                          const midAngle = startAngle + angle / 2;
                          const midAngleRad = (midAngle * Math.PI) / 180;
                          const labelRadius = (radius + innerRadius) / 2;
                          const labelX =
                            centerX + labelRadius * Math.cos(midAngleRad);
                          const labelY =
                            centerY + labelRadius * Math.sin(midAngleRad);

                          currentAngle += angle;

                          return (
                            <g key={index}>
                              <path d={pathData} fill={tipo.color} stroke="none" />
                              {percentage > 0.08 && (
                                <text
                                  x={labelX}
                                  y={labelY}
                                  textAnchor="middle"
                                  dominantBaseline="middle"
                                  fill="#ffffff"
                                  fontSize="11"
                                  fontWeight="700"
                                >
                                  {(percentage * 100).toFixed(0)}%
                                </text>
                              )}
                            </g>
                          );
                        });
                      })()}
                    </svg>
                    <div className="donut-center">
                      <span className="donut-total">
                        {clientsData.length}
                      </span>
                      <span className="donut-label">Clientes</span>
                    </div>
                  </div>
                <div className="segments-list">
                {tipoPessoaData.map((tipo, index) => (
                  <div key={index} className="segment-item">
                    <div className="segment-header">
                      <div
                        className="segment-color"
                        style={{ backgroundColor: tipo.color }}
                      />
                      <span className="segment-name">{tipo.name}</span>
                      <span className="segment-count">{tipo.count?.toLocaleString('pt-BR')}</span>
                    </div>
                    <div className="segment-bar">
                      <div
                        className="segment-progress"
                        style={{
                          width: `${maxFaturamento > 0 ? (tipo.faturamento / maxFaturamento) * 100 : 0}%`,
                          backgroundColor: tipo.color,
                        }}
                      />
                    </div>
                    <div className="segment-value">{formatNumber(tipo.faturamento)}</div>
                  </div>
                ))}
                </div>
              </div>
            </div>
          </MainContent>

        </>
      )}

      {/* ========== TAB ESTATÍSTICAS ========== */}
      {activeTab === "estatisticas" && (
        <div className="stats-tab">
          {/* Container para todas as linhas de gráficos */}
          <div className="charts-container">
            {/* Linha 1: Donut, Top Estados e Mapa */}
            <div
              className="charts-row"
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr",
                gap: "20px",
              }}
            >
              {/* Card Donut Chart - Distribuição por Segmento */}
              <div className="stats-section chart-card">
                <div className="section-header">
                  <GoStack />
                  <h4>Distribuição por Entidade</h4>
                </div>
                <div className="donut-wrapper" style={{ display: "flex", flexDirection: "row", justifyContent: "center", alignItems: "center", height: "100%", width: "100%", gap: "20px", paddingBottom: "40px" }}>
                  <div className="donut-chart">
                    <svg width="180" height="180" viewBox="0 0 200 200">
                      {(() => {
                        const colors = ["#3b82f6","#10b981","#f59e0b"];
                        
                        // Usar tipoPessoaData (backend) em vez de mock
                        const segmentos = (tipoPessoaData && tipoPessoaData.length > 0) 
                            ? tipoPessoaData.map((d, i) => ({...d, color: colors[i % colors.length] }))
                            : [
                                { name: "PJ", count: 0, faturamento: 0, color: colors[0] },
                                { name: "PF", count: 0, faturamento: 0, color: colors[1] }
                              ];

                        const total = segmentos.reduce((sum, seg) => sum + (seg.count || 0), 0);
                        let currentAngle = -90;
                        const radius = 80;
                        const innerRadius = 50;
                        const cx = 100,
                          cy = 100;

                        return segmentos.map((seg, index) => {
                          if (total === 0) return null;
                          const percentage = (seg.count || 0) / total;
                          const angle = percentage * 360;
                          const startAngle = currentAngle;
                          const endAngle = currentAngle + angle;

                          const startRad = (startAngle * Math.PI) / 180;
                          const endRad = (endAngle * Math.PI) / 180;
                          const largeArc = angle > 180 ? 1 : 0;

                          const x1 = cx + radius * Math.cos(startRad);
                          const y1 = cy + radius * Math.sin(startRad);
                          const x2 = cx + radius * Math.cos(endRad);
                          const y2 = cy + radius * Math.sin(endRad);
                          const ix1 = cx + innerRadius * Math.cos(startRad);
                          const iy1 = cy + innerRadius * Math.sin(startRad);
                          const ix2 = cx + innerRadius * Math.cos(endRad);
                          const iy2 = cy + innerRadius * Math.sin(endRad);

                          const path = `M ${ix1} ${iy1} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} L ${ix2} ${iy2} A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${ix1} ${iy1} Z`;

                          // Label Position
                          const midAngle = startAngle + angle / 2;
                          const midRad = (midAngle * Math.PI) / 180;
                          const labelR = (radius + innerRadius) / 2;
                          const labelX = cx + labelR * Math.cos(midRad);
                          const labelY = cy + labelR * Math.sin(midRad);

                          currentAngle += angle;

                          return (
                            <g key={index}>
                              <path d={path} fill={seg.color} />
                              {percentage > 0.08 && (
                                <text
                                  x={labelX}
                                  y={labelY}
                                  textAnchor="middle"
                                  dominantBaseline="middle"
                                  fill="#fff"
                                  fontSize="11"
                                  fontWeight="600"
                                >
                                  {(percentage * 100).toFixed(0)}%
                                </text>
                              )}
                            </g>
                          );
                        });
                      })()}
                    </svg>
                    <div className="donut-center">
                      <span className="donut-total">
                        {totals?.clientes || 0}
                      </span>
                      <span className="donut-label">Clientes</span>
                    </div>
                  </div>
                  <div className="categories-list">
                    {(() => {
                        const colors = ["#3b82f6","#10b981","#f59e0b"];
                        const segmentos = (tipoPessoaData && tipoPessoaData.length > 0) 
                            ? tipoPessoaData.map((d, i) => ({...d, color: colors[i % colors.length] }))
                            : [];

                      return segmentos.map((seg, index) => (
                        <div key={index} className="category-item">
                          <div className="category-header">
                            <div
                              className="category-color"
                              style={{ backgroundColor: seg.color }}
                            ></div>
                            <span className="category-name">{seg.name}</span>
                            <span className="category-count">{seg.count}</span>
                          </div>
                        </div>
                      ));
                    })()}
                  </div>
                </div>
              </div>

              {/* Card Top Estados por Faturamento - Sofisticado */}
              <div className="stats-section chart-card">
                <div className="section-header">
                  <GoLocation />
                  <h4>Top Estados</h4>
                </div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "6px",
                    padding: "4px 0",
                    maxHeight: "320px",
                    overflowY: "auto",
                    paddingRight: "4px",
                  }}
                  className="custom-scrollbar"
                >
                  {(() => {
                    const ufMap = new Map();
                    // Usar geoData do backend
                    const geoList = dashboardData?.geo || [];

                    const regioes = {
                      SP: "Sudeste",
                      RJ: "Sudeste",
                      MG: "Sudeste",
                      ES: "Sudeste",
                      RS: "Sul",
                      PR: "Sul",
                      SC: "Sul",
                      BA: "Nordeste",
                      PE: "Nordeste",
                      CE: "Nordeste",
                      MA: "Nordeste",
                      PI: "Nordeste",
                      RN: "Nordeste",
                      PB: "Nordeste",
                      AL: "Nordeste",
                      SE: "Nordeste",
                      GO: "Centro-Oeste",
                      MT: "Centro-Oeste",
                      MS: "Centro-Oeste",
                      DF: "Centro-Oeste",
                      AM: "Norte",
                      PA: "Norte",
                      AC: "Norte",
                      RO: "Norte",
                      RR: "Norte",
                      AP: "Norte",
                      TO: "Norte",
                    };
                    
                    if (geoList.length > 0) {
                        geoList.forEach((d) => {
                             ufMap.set(d.id, { uf: d.id, faturamento: d.faturamento, count: d.count });
                        });
                    }

                    const totalFat = Array.from(ufMap.values()).reduce(
                      (sum, d) => sum + d.faturamento,
                      0
                    );
                    const sorted = Array.from(ufMap.values()).sort(
                      (a, b) => b.faturamento - a.faturamento
                    );
                    const maxFat = sorted[0]?.faturamento || 1;
                    const nomeEstados = {
                      SP: "São Paulo",
                      RJ: "Rio de Janeiro",
                      MG: "Minas Gerais",
                      ES: "Espírito Santo",
                      RS: "Rio Grande do Sul",
                      PR: "Paraná",
                      SC: "Santa Catarina",
                      BA: "Bahia",
                      PE: "Pernambuco",
                      GO: "Goiás",
                      // ... (adicionar outros se necessário)
                    };
                    const rankColors = [
                      "#10b981",
                      "#3b82f6",
                      "#8b5cf6",
                      "#f59e0b",
                      "#64748b",
                    ];

                    return sorted.map((estado, index) => {
             
                      const percent = (estado.faturamento / totalFat) * 100;
                      const barWidth = (estado.faturamento / maxFat) * 100;
                      const color = rankColors[index];

                      return (
                        <div
                          key={estado.uf}
                          style={{
                            background: `linear-gradient(135deg, ${color}08 0%, transparent 100%)`,
                            borderRadius: "10px",
                            padding: "12px 14px",
                            border: `1px solid ${color}20`,
                            transition: "all 0.2s ease",
                            cursor: "pointer",
                            position: "relative",
                            overflow: "hidden",
                            minHeight: "52px",
                            flexShrink: 0,
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = "translateX(4px)";
                            e.currentTarget.style.borderColor = `${color}40`;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "translateX(0)";
                            e.currentTarget.style.borderColor = `${color}20`;
                          }}
                        >
                          {/* Barra de progresso de fundo */}
                          <div
                            style={{
                              position: "absolute",
                              left: 0,
                              top: 0,
                              height: "100%",
                              width: `${barWidth}%`,
                              background: `linear-gradient(90deg, ${color}15 0%, ${color}05 100%)`,
                              borderRadius: "10px",
                              transition: "width 0.5s ease",
                            }}
                          />

                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              position: "relative",
                              zIndex: 1,
                            }}
                          >
                            {/* Lado esquerdo */}
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "12px",
                              }}
                            >
                              {/* Badge de posição */}
                              <div
                                style={{
                                  width: "28px",
                                  height: "28px",
                                  borderRadius: "8px",
                                  background:
                                    index < 3
                                      ? `linear-gradient(135deg, ${color} 0%, ${color}cc 100%)`
                                      : "rgba(100, 116, 139, 0.2)",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: "0.75em",
                                  fontWeight: 700,
                                  color: index < 3 ? "#fff" : "#94a3b8",
                                  boxShadow:
                                    index < 3 ? `0 2px 8px ${color}40` : "none",
                                }}
                              >
                                {index + 1}
                              </div>

                              {/* Info do estado */}
                              <div>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "6px",
                                  }}
                                >
                                  <span
                                    style={{
                                      fontSize: "0.9em",
                                      fontWeight: 700,
                                      color: theme.text,
                                    }}
                                  >
                                    {estado.uf}
                                  </span>
                                  <span
                                    style={{
                                      fontSize: "0.75em",
                                      color: "#64748b",
                                    }}
                                  >
                                    •
                                  </span>
                                  <span
                                    style={{
                                      fontSize: "0.8em",
                                      fontWeight: 500,
                                      color: "#94a3b8",
                                    }}
                                  >
                                    {nomeEstados[estado.uf] || estado.uf}
                                  </span>
                                </div>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                    marginTop: "2px",
                                  }}
                                >
                                  <span
                                    style={{
                                      fontSize: "0.65em",
                                      color: "#64748b",
                                      background: "rgba(100, 116, 139, 0.1)",
                                      padding: "2px 6px",
                                      borderRadius: "4px",
                                    }}
                                  >
                                    {regioes[estado.uf] || "Brasil"}
                                  </span>
                                  <span
                                    style={{
                                      fontSize: "0.65em",
                                      color: "#64748b",
                                    }}
                                  >
                                    {estado.count} cliente
                                    {estado.count > 1 ? "s" : ""}
                                  </span>
                                </div>
                              </div>
                            </div>

                            {/* Lado direito - Valores */}
                            <div style={{ textAlign: "right" }}>
                              <div
                                style={{
                                  fontSize: "0.95em",
                                  fontWeight: 700,
                                  color: color,
                                }}
                              >
                                {formatNumber(estado.faturamento)}
                              </div>
                              <div
                                style={{
                                  fontSize: "0.65em",
                                  color: "#64748b",
                                  background: `${color}15`,
                                  padding: "2px 8px",
                                  borderRadius: "10px",
                                  marginTop: "2px",
                                  display: "inline-block",
                                }}
                              >
                                {percent.toFixed(1)}%
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>

              {/* Card Mapa do Brasil - D3.js */}
              <BrazilMapCard
                 geoStats={dashboardData?.geo || []} 
                 formatNumber={formatNumber}
              />
            </div>

            {/* Linha 2: Rankings */}
            <div className="rankings-row">
              {/* Top Clientes por Faturamento */}
              <div className="ranking-card">
                <div className="ranking-header">
                  <GoGraph />
                  <h4>Top Clientes por Faturamento</h4>
                </div>
                <div className="ranking-list">
                  {(clientsData || [])
                    .sort((a, b) => (b.faturamento || 0) - (a.faturamento || 0))
                    .slice(0, 5)
                    .map((client, index) => (
                      <div key={client.id || `rank-fat-${index}`} className="ranking-item">
                        <div className="ranking-position">{index + 1}</div>
                        <div className="ranking-thumb faturamento">
                          <GoPeople />
                        </div>
                        <div className="ranking-info">
                          <span className="ranking-name">{client.nome}</span>
                          <div className="ranking-tags">
                            <span className="category-tag">
                              {client.segmento || "Geral"}
                            </span>
                            <span className="code-tag">{client.uf || "ND"}</span>
                          </div>
                        </div>
                        <span className="ranking-value faturamento" style={{ fontSize: "1.2em" }}>
                          {formatNumber(client.faturamento || 0)}
                        </span>
                      </div>
                    ))}
                </div>
              </div>



              {/* Top Clientes por Impostos */}
              <div className="ranking-card">
                <div className="ranking-header">
                  <GoLaw />
                  <h4>Maior Carga Tributária</h4>
                </div>
                <div className="ranking-list">
                  {(clientsData || [])
                    .map((c) => ({
                      ...c,
                      totalImpostos:
                        (c.icms || 0) +
                        (c.pisCofins || 0) +
                        (c.ibs || 0) +
                        (c.cbs || 0) +
                        (c.is || 0),
                    }))
                    .sort((a, b) => b.totalImpostos - a.totalImpostos)
                    .slice(0, 5)
                    .map((client, index) => (
                      <div key={client.id || `rank-tax-${index}`} className="ranking-item">
                        <div className="ranking-position">{index + 1}</div>
                        <div className="ranking-thumb impostos">
                          <GoLaw />
                        </div>
                        <div className="ranking-info">
                          <span className="ranking-name">{client.nome}</span>
                          <div className="ranking-tags">
                            <span className="category-tag">
                              {client.segmento || "Geral"}
                            </span>
                          </div>
                        </div>
                        <span className="ranking-value impostos" style={{ fontSize: "1.2em" }}>
                          {formatNumber(client.totalImpostos)}
                        </span>
                      </div>
                    ))}
                </div>
              </div>
            </div>

            {/* Linha 3: KPIs Executivos */}
            <div className="executive-kpis-row" style={{ display: "flex", gap: "20px", justifyContent: "space-between" }}>
              <div className="exec-kpi-card" style={{ flex: 1 }}>
                <div className="kpi-icon blue">
                  <GoGraph />
                </div>
                <div className="kpi-content">
                  <span className="kpi-value">
                    {formatNumber(totals.faturamento)}
                  </span>
                  <span className="kpi-label">Faturamento Total</span>
                  <span className="kpi-change positive">
                    +15.2% vs mês anterior
                  </span>
                </div>
              </div>
              <div className="exec-kpi-card" style={{ flex: 1 }}>
                <div className="kpi-icon green">
                  <GoPeople />
                </div>
                <div className="kpi-content">
                  <span className="kpi-value">{totals.clientes}</span>
                  <span className="kpi-label">Clientes Ativos</span>
                  <span className="kpi-change positive">+8 novos este mês</span>
                </div>
              </div>
              <div className="exec-kpi-card" style={{ flex: 1 }}>
                <div className="kpi-icon orange">
                  <GoArrowUp />
                </div>
                <div className="kpi-content">
                  <span className="kpi-value">
                    {formatNumber(totals.ticketMedio)}
                  </span>
                  <span className="kpi-label">Ticket Médio</span>
                  <span className="kpi-change positive">
                    +5.8% vs mês anterior
                  </span>
                </div>
              </div>
              <div className="exec-kpi-card" style={{ flex: 1 }}>
                <div className="kpi-icon red">
                  <GoLaw />
                </div>
                <div className="kpi-content">
                  <span className="kpi-value">
                    {formatNumber(totals.impostos)}
                  </span>
                  <span className="kpi-label">Carga Tributária</span>
                  <span className="kpi-change negative">
                    +3.2% vs mês anterior
                  </span>
                </div>
              </div>

            </div>

            {/* Linha 4: Gráficos Avançados */}
            <div className="advanced-charts-row">
              {/* Gráfico de Evolução de Faturamento */}
              <div className="advanced-chart-card">
                <div className="chart-header-advanced">
                  <div className="chart-title-group">
                    <HiOutlineChartBar className="chart-icon" />
                    <h4>Evolução do Faturamento</h4>
                  </div>
                  <div className="chart-period-selector">
                    <button className="period-btn active">12M</button>
                    <button className="period-btn">6M</button>
                    <button className="period-btn">3M</button>
                  </div>
                </div>
                <div className="chart-body">
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart
                      data={dashboardData?.evolucao || []}
                      margin={{ top: 10, right: 20, left: 10, bottom: 5 }}
                      barGap={2}
                    >
                      <defs>
                        <linearGradient
                          id="gradFaturamentoClientes"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="0%"
                            stopColor="#10b981"
                            stopOpacity={1}
                          />
                          <stop
                            offset="100%"
                            stopColor="#059669"
                            stopOpacity={1}
                          />
                        </linearGradient>
                      </defs>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke={theme.border}
                        vertical={false}
                      />
                      <XAxis
                        dataKey="mes"
                        tick={{ fill: theme.text, fontSize: 11, opacity: 0.7 }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis
                        tick={{ fill: theme.text, fontSize: 10, opacity: 0.6 }}
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={(value) =>
                          `${(value / 1000000).toFixed(1)}M`
                        }
                        width={45}
                      />
                      <Tooltip
                        cursor={{ fill: theme.tableRowHover }}
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            const data = payload[0].payload;
                            return (
                              <div
                                style={{
                                  background: "rgba(17, 24, 39, 0.95)",
                                  border: "1px solid rgba(255, 255, 255, 0.1)",
                                  borderRadius: "12px",
                                  padding: "16px 20px",
                                  minWidth: "200px",
                                  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.4)",
                                  backdropFilter: "blur(10px)",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    marginBottom: "12px",
                                    paddingBottom: "10px",
                                    borderBottom:
                                      "1px solid rgba(255, 255, 255, 0.1)",
                                  }}
                                >
                                  <span
                                    style={{
                                      fontSize: "0.95em",
                                      fontWeight: 600,
                                      color: "#fff",
                                    }}
                                  >
                                    {data.mes}
                                  </span>
                                  <span
                                    style={{
                                      fontSize: "0.75em",
                                      color: "#9ca3af",
                                      background: "rgba(255, 255, 255, 0.05)",
                                      padding: "3px 8px",
                                      borderRadius: "4px",
                                    }}
                                  >
                                    {data.novos} novo
                                    {data.novos !== 1 ? "s" : ""}
                                  </span>
                                </div>
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                  }}
                                >
                                  <span
                                    style={{
                                      fontSize: "0.85em",
                                      color: "#9ca3af",
                                    }}
                                  >
                                    Faturamento
                                  </span>
                                  <span
                                    style={{
                                      fontSize: "1.1em",
                                      fontWeight: 700,
                                      color: "#3b82f6",
                                    }}
                                  >
                                    {formatNumber(data.faturamento)}
                                  </span>
                                </div>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                      <Bar
                        dataKey="faturamento"
                        name="Faturamento"
                        fill="url(#gradFaturamentoClientes)"
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Gráfico de Composição Tributária */}
              <div className="stats-section chart-card">
                <div className="section-header">
                  <GoLaw />
                  <h4>Composição Tributária</h4>
                </div>
                <div className="donut-wrapper" style={{ display: "flex", flexDirection: "row", justifyContent: "center", alignItems: "center", height: "100%", width: "100%", gap: "20px", paddingBottom: "40px" }}>
                  <div className="donut-chart">
                    {(() => {
                      const det = dashboardData?.impostosDetalhados || {};
                      const taxes = [
                        { name: "ICMS", value: det.icms || 0, color: "#ef4444" },
                        {
                          name: "PIS/COFINS",
                          value: det.pisCofins || 0,
                          color: "#3b82f6",
                        },
                        { name: "IBS", value: det.ibs || 0, color: "#10b981" },
                        { name: "CBS", value: det.cbs || 0, color: "#f59e0b" },
                        { name: "IS", value: det.is || 0, color: "#8b5cf6" },
                      ];
                      const activeTaxes = taxes.filter((t) => t.value > 0);
                      const total = taxes.reduce((sum, t) => sum + t.value, 0);

                      let currentAngle = -90;
                      const radius = 80;
                      const innerRadius = 50;
                      const cx = 100,
                        cy = 100;

                      return (
                        <>
                          <svg width="180" height="180" viewBox="0 0 200 200">
                            {activeTaxes.map((tax, index) => {
                              const percentage = tax.value / total;
                              const angle = percentage * 360;
                              const startAngle = currentAngle;
                              const startRad = (startAngle * Math.PI) / 180;
                              const endRad =
                                ((startAngle + angle) * Math.PI) / 180;
                              const largeArc = angle > 180 ? 1 : 0;

                              const x1 = cx + radius * Math.cos(startRad);
                              const y1 = cy + radius * Math.sin(startRad);
                              const x2 = cx + radius * Math.cos(endRad);
                              const y2 = cy + radius * Math.sin(endRad);
                              const ix1 = cx + innerRadius * Math.cos(startRad);
                              const iy1 = cy + innerRadius * Math.sin(startRad);
                              const ix2 = cx + innerRadius * Math.cos(endRad);
                              const iy2 = cy + innerRadius * Math.sin(endRad);

                              const path = `M ${ix1} ${iy1} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} L ${ix2} ${iy2} A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${ix1} ${iy1} Z`;

                              const midAngle = startAngle + angle / 2;
                              const midRad = (midAngle * Math.PI) / 180;
                              const labelR = (radius + innerRadius) / 2;
                              const labelX = cx + labelR * Math.cos(midRad);
                              const labelY = cy + labelR * Math.sin(midRad);

                              currentAngle += angle;

                              return (
                                <g key={index}>
                                  <path d={path} fill={tax.color} />
                                  {percentage > 0.08 && (
                                    <text
                                      x={labelX}
                                      y={labelY}
                                      textAnchor="middle"
                                      dominantBaseline="middle"
                                      fill="#fff"
                                      fontSize="12"
                                      fontWeight="600"
                                    >
                                      {(percentage * 100).toFixed(0)}%
                                    </text>
                                  )}
                                </g>
                              );
                            })}
                          </svg>
                          <div className="donut-center">
                            <span className="donut-total">
                              {(total / 1000000).toFixed(1)}M
                            </span>
                            <span className="donut-label">Total</span>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                  <div className="categories-list">
                    {(() => {
                      const det = dashboardData?.impostosDetalhados || {};
                      const taxes = [
                        { name: "ICMS", value: det.icms || 0, color: "#ef4444" },
                        {
                          name: "PIS/COFINS",
                          value: det.pisCofins || 0,
                          color: "#3b82f6",
                        },
                        { name: "IBS", value: det.ibs || 0, color: "#10b981" },
                        { name: "CBS", value: det.cbs || 0, color: "#f59e0b" },
                        { name: "IS", value: det.is || 0, color: "#8b5cf6" },
                      ];

                      const formatValue = (val) =>
                        val === 0
                          ? "R$ 0,00"
                          : `R$ ${(val / 1000000).toFixed(1)}M`;

                      return taxes.map((tax, index) => (
                        <div key={index} className="category-item">
                          <div className="category-header">
                            <div
                              className="category-color"
                              style={{ backgroundColor: tax.color }}
                            ></div>
                            <span className="category-name">{tax.name}</span>
                            <span className="category-count">
                              {formatValue(tax.value)}
                            </span>
                          </div>
                        </div>
                      ));
                    })()}
                  </div>
                </div>
              </div>
            </div>




          </div>
        </div>
      )}

      {/* ========== TAB REFORMA TRIBUTÁRIA ========== */}
      {activeTab === "reforma" && (
        <div className="reforma-tab">
          {/* Loading State */}
          {reformaLoading && (
            <div className="loading-container" style={{ textAlign: 'center', padding: '60px 20px' }}>
              <FiLoader className="spin" style={{ fontSize: '48px', color: theme.primary, marginBottom: '16px' }} />
              <p style={{ color: theme.textSecondary }}>Carregando dados da Reforma Tributária...</p>
            </div>
          )}

          {/* Error State */}
          {reformaError && !reformaLoading && (
            <div className="error-container" style={{ textAlign: 'center', padding: '60px 20px' }}>
              <GoAlert style={{ fontSize: '48px', color: '#ef4444', marginBottom: '16px' }} />
              <p style={{ color: theme.text, marginBottom: '8px' }}>{reformaError}</p>
              <button onClick={fetchReformaData} style={{ padding: '8px 16px', borderRadius: '8px', background: theme.primary, color: '#fff', border: 'none', cursor: 'pointer' }}>
                Tentar Novamente
              </button>
            </div>
          )}

          {/* Content */}
          {!reformaLoading && !reformaError && (
            <>
              {/* Botões de Cenário */}
              <div className="cenario-buttons">
                <button 
                  className={`cenario-btn ${selectedCenario === "2027" ? "active" : ""}`}
                  onClick={() => setSelectedCenario("2027")}
                >
                  <span className="cenario-year">2027</span>
                  <span className="cenario-label">Início IVA Dual</span>
                </button>
                <button 
                  className={`cenario-btn ${selectedCenario === "2028" ? "active" : ""}`}
                  onClick={() => setSelectedCenario("2028")}
                >
                  <span className="cenario-year">2028</span>
                  <span className="cenario-label">Transição 0%</span>
                </button>
                <button 
                  className={`cenario-btn ${selectedCenario === "2029" ? "active" : ""}`}
                  onClick={() => setSelectedCenario("2029")}
                >
                  <span className="cenario-year">2029</span>
                  <span className="cenario-label">Transição 10%</span>
                </button>
                <button 
                  className={`cenario-btn ${selectedCenario === "2030" ? "active" : ""}`}
                  onClick={() => setSelectedCenario("2030")}
                >
                  <span className="cenario-year">2030</span>
                  <span className="cenario-label">Transição 20%</span>
                </button>
                <button 
                  className={`cenario-btn ${selectedCenario === "2031" ? "active" : ""}`}
                  onClick={() => setSelectedCenario("2031")}
                >
                  <span className="cenario-year">2031</span>
                  <span className="cenario-label">Transição 30%</span>
                </button>
                <button 
                  className={`cenario-btn ${selectedCenario === "2032" ? "active" : ""}`}
                  onClick={() => setSelectedCenario("2032")}
                >
                  <span className="cenario-year">2032</span>
                  <span className="cenario-label">Transição 40%</span>
                </button>
                <button 
                  className={`cenario-btn ${selectedCenario === "2033" ? "active" : ""}`}
                  onClick={() => setSelectedCenario("2033")}
                >
                  <span className="cenario-year">2033</span>
                  <span className="cenario-label">IVA Dual Pleno</span>
                </button>
              </div>

              {/* Linha 1 - Visão Geral */}
              <div className="kpi-cards main-stats">
                <div className="kpi-card primary">
                  <div className="card-content">
                    <div className="thumb">
                      <GoGraph />
                    </div>
                    <div className="text">
                      <p>{formatNumber(reformaKpis.faturamentoTotal)}</p>
                      <p>Faturamento Total</p>
                    </div>
                  </div>
                </div>

                <div className="kpi-card warning">
                  <div className="card-content">
                    <div className="thumb">
                      <GoPeople />
                    </div>
                    <div className="text">
                      <p>{reformaKpis.totalClientes.toLocaleString('pt-BR')}</p>
                      <p>Total de Clientes</p>
                    </div>
                  </div>
                </div>

                <div className="kpi-card info">
                  <div className="card-content">
                    <div className="thumb">
                      <GoOrganization />
                    </div>
                    <div className="text">
                      <p>{reformaKpis.totalSegmentos.toLocaleString('pt-BR')}</p>
                      <p>Segmentos Atuantes</p>
                    </div>
                  </div>
                </div>

                <div className="kpi-card cte">
                  <div className="card-content">
                    <div className="thumb">
                      <GoGraph />
                    </div>
                    <div className="text">
                      <p>{(reformaKpis.aliquotaCBS + reformaKpis.aliquotaIBS).toFixed(2)}%</p>
                      <p>Alíquota Efetiva ({selectedCenario})</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Linha 2 - Débitos Tributários Atuais */}
              <div className="kpi-cards secondary-stats">
                <div className="kpi-card compact warning with-progress">
                  <div className="card-content">
                    <div className="thumb">
                      <GoCreditCard />
                    </div>
                    <div className="text">
                      <p>{formatNumber(reformaKpis.debitosAtuais)}</p>
                      <p>Débitos Tributários Atuais</p>
                    </div>
                  </div>
                </div>

                <div className="kpi-card compact">
                  <div className="card-content">
                    <div className="thumb">
                      <GoLaw />
                    </div>
                    <div className="text">
                      <p>{formatNumber(reformaKpis.icms)}</p>
                      <p>ICMS</p>
                    </div>
                  </div>
                </div>

                <div className="kpi-card compact nfe">
                  <div className="card-content">
                    <div className="thumb">
                      <GoLaw />
                    </div>
                    <div className="text">
                      <p>{formatNumber(reformaKpis.cofins)}</p>
                      <p>COFINS</p>
                    </div>
                  </div>
                </div>

                <div className="kpi-card compact info">
                  <div className="card-content">
                    <div className="thumb">
                      <GoLaw />
                    </div>
                    <div className="text">
                      <p>{formatNumber(reformaKpis.pis)}</p>
                      <p>PIS</p>
                    </div>
                  </div>
                </div>

                <div className="kpi-card compact cte">
                  <div className="card-content">
                    <div className="thumb">
                      <GoLaw />
                    </div>
                    <div className="text">
                      <p>{formatNumber(reformaKpis.iss + reformaKpis.ipi)}</p>
                      <p>ISS + IPI</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Linha 3 - IVA Dual Projetado */}
              <div className="kpi-cards secondary-stats">
                <div className="kpi-card compact success with-progress">
                  <div className="card-content">
                    <div className="thumb">
                      <GoCreditCard />
                    </div>
                    <div className="text">
                      <p>{formatNumber(reformaKpis.ivaDual)}</p>
                      <p>IVA Dual Projetado ({selectedCenario})</p>
                    </div>
                  </div>
                </div>

                <div className="kpi-card compact success">
                  <div className="card-content">
                    <div className="thumb">
                      <GoLaw />
                    </div>
                    <div className="text">
                      <p>{formatNumber(reformaKpis.cbs)}</p>
                      <p>CBS</p>
                    </div>
                  </div>
                </div>

                <div className="kpi-card compact info">
                  <div className="card-content">
                    <div className="thumb">
                      <GoLaw />
                    </div>
                    <div className="text">
                      <p>{reformaKpis.aliquotaCBS.toFixed(2)}%</p>
                      <p>Alíquota CBS</p>
                    </div>
                  </div>
                </div>

                <div className="kpi-card compact primary">
                  <div className="card-content">
                    <div className="thumb">
                      <GoLaw />
                    </div>
                    <div className="text">
                      <p>{formatNumber(reformaKpis.ibs)}</p>
                      <p>IBS</p>
                    </div>
                  </div>
                </div>

                <div className="kpi-card compact cte">
                  <div className="card-content">
                    <div className="thumb">
                      <GoLaw />
                    </div>
                    <div className="text">
                      <p>{reformaKpis.aliquotaIBS.toFixed(2)}%</p>
                      <p>Alíquota IBS</p>
                    </div>
                  </div>
                </div>
              </div>
              
               {/* Busca de Clientes */}
              <div className="reforma-search">
                <div className="search-input-wrapper">
                  <GoSearch className="search-icon" />
                  <input
                    type="text"
                    placeholder="Buscar clientes por nome ou CNPJ..."
                    className="search-input"
                    value={reformaSearchTerm}
                    onChange={(e) => setReformaSearchTerm(e.target.value)}
                  />
                </div>
              </div>

              {/* Tabela de Produtos com Impostos + Card Impacto */}
              <div className="reforma-content-row">
                <div className="reforma-products-table">
                  <div className="table-header">
                    <div className="header-cell produto">Cliente</div>
                    <div className="header-cell">ICMS</div>
                    <div className="header-cell">PIS/COFINS</div>
                    <div className="header-cell">ISS/IPI</div>
                    <div className="header-cell">CBS</div>
                    <div className="header-cell">IBS</div>
                  </div>
                  <div className="table-body">
                    {((reformaSearchTerm.trim() ? reformaSearchResults : reformaClients) || []).map((client, index) => {
                      // Obter alíquotas e redução do cenário selecionado
                      const cenarioAno = parseInt(selectedCenario);
                      const cenario = CRONOGRAMA_REFORMA[cenarioAno] || CRONOGRAMA_REFORMA["2027"];
                      const aliquotaCBS = cenario.cbs;
                      const aliquotaIBS = cenario.ibs;
                      const reducaoAntigos = cenario.reducaoAntigos || 0;
                      
                      // Valores base dos impostos atuais
                      const icmsBase = client.icmsBase || client.icms || (client.faturamento || 0) * 0.12;
                      const pisCofinsBase = client.pisCofinsBase || client.pisCofins || (client.faturamento || 0) * 0.0925;
                      const isBase = client.is || (client.faturamento || 0) * 0.02;
                      
                      // Aplicar redução gradual aos impostos antigos
                      const icmsAtual = icmsBase * (1 - reducaoAntigos);
                      const pisCofinsAtual = pisCofinsBase * (1 - reducaoAntigos);
                      const isAtual = isBase * (1 - reducaoAntigos);
                      
                      // Calcular novos impostos (CBS e IBS)
                      const cbsAtual = client.cbs || (client.faturamento || 0) * aliquotaCBS;
                      const ibsAtual = client.ibs || (client.faturamento || 0) * aliquotaIBS;
                      
                      return (
                        <div
                          key={client.id || `client-${index}`}
                          className="product-row"
                          style={{
                            "--product-color": getClientColor(client.cnpj),
                            cursor: "pointer",
                          }}
                          onClick={() => setSelectedClient(client)}
                        >
                          <div className="product-info">
                            <span className="product-code">{client.cnpj}</span>
                            <span className="product-name">{client.nome}</span>
                          </div>
                          <div className="tax-value icms">
                            {formatNumber(icmsAtual)}
                          </div>
                          <div className="tax-value pis">
                            {formatNumber(pisCofinsAtual)}
                          </div>
                          <div className="tax-value ipi">
                            {formatNumber(isAtual)}
                          </div>
                          <div className="tax-value cbs">
                            {formatNumber(cbsAtual)}
                          </div>
                          <div className="tax-value ibs">
                            {formatNumber(ibsAtual)}
                          </div>
                        </div>
                      );
                    })}
                    
                    {/* Observer target for infinite scroll */}
                    {reformaHasMore && (
                      <div ref={reformaObserverTarget} style={{ padding: '20px', textAlign: 'center' }}>
                        {reformaIsLoadingMore && (
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                            <FiLoader style={{ animation: 'spin 1s linear infinite' }} />
                            <span>Carregando mais clientes...</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Card Impacto da Reforma */}
                <div className="impacto-sidebar">
                  {/* Card Principal - Impacto */}
                  <div className="impacto-main-card">
                    <div className="impacto-header">
                      <div className="impacto-icon">
                        <GoGraph />
                      </div>
                      <span className={`impacto-badge ${Math.abs(reformaKpis.impacto) > 50000 ? 'alto' : Math.abs(reformaKpis.impacto) > 10000 ? 'medio' : 'baixo'}`}>
                        {Math.abs(reformaKpis.impacto) > 50000 ? 'ALTO IMPACTO' : Math.abs(reformaKpis.impacto) > 10000 ? 'MÉDIO IMPACTO' : 'BAIXO IMPACTO'}
                      </span>
                    </div>
                    <span className="impacto-title">Impacto da Reforma</span>
                    <span className="impacto-value">{formatNumber(Math.abs(reformaKpis.impacto))}</span>
                    <span className="impacto-desc">
                      {reformaKpis.impacto >= 0 ? 'Aumento tributário projetado' : 'Economia tributária projetada'}
                    </span>
                    <div className="impacto-variation">
                      {reformaKpis.impacto >= 0 ? <GoArrowUp /> : <GoArrowDown />}
                      <span>{reformaKpis.impacto >= 0 ? '+' : ''}{reformaKpis.variacaoPercentual.toFixed(1)}%</span>
                      <span className="variation-label">vs. sistema atual</span>
                    </div>
                  </div>

                  {/* Card Comparativo */}
                  <div className="impacto-compare-card">
                    <span className="compare-title">Comparativo de Carga</span>
                    <div className="compare-items">
                      <div className="compare-item">
                        <span className="compare-label">Sistema Atual</span>
                        <span className="compare-value atual">{formatNumber(reformaKpis.debitosAtuais)}</span>
                      </div>
                      <div className="compare-item">
                        <span className="compare-label">IVA Dual ({selectedCenario})</span>
                        <span className="compare-value novo">{formatNumber(reformaKpis.ivaDual)}</span>
                      </div>
                      <div className="compare-item highlight">
                        <span className="compare-label">Diferença</span>
                        <span className="compare-value diff">{reformaKpis.impacto >= 0 ? '+' : ''}{formatNumber(reformaKpis.impacto)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Card Alíquotas */}
                  <div className="impacto-aliquotas-card">
                    <span className="aliquotas-title">Alíquotas Projetadas</span>
                    <div className="aliquota-row">
                      <div className="aliquota-item">
                        <span className="aliquota-sigla">CBS</span>
                        <span className="aliquota-valor">{reformaKpis.aliquotaCBS.toFixed(2)}%</span>
                      </div>
                      <div className="aliquota-item">
                        <span className="aliquota-sigla">IBS</span>
                        <span className="aliquota-valor">{reformaKpis.aliquotaIBS.toFixed(2)}%</span>
                      </div>
                    </div>
                    <div className="aliquota-total">
                      <span>Alíquota Efetiva Total</span>
                      <span className="total-valor">{(reformaKpis.aliquotaCBS + reformaKpis.aliquotaIBS).toFixed(2)}%</span>
                    </div>
                  </div>

                  {/* Card Timeline */}
                  <div className="impacto-timeline-card">
                    <span className="timeline-title">Cronograma de Transição</span>
                    <div className="timeline-steps">
                      <div className="timeline-step active">
                        <span className="step-year">2027</span>
                        <span className="step-desc">Início</span>
                      </div>
                      <div className="timeline-step">
                        <span className="step-year">2029</span>
                        <span className="step-desc">40%</span>
                      </div>
                      <div className="timeline-step">
                        <span className="step-year">2033</span>
                        <span className="step-desc">100%</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Gráficos de Barras */}
              <div className="reforma-charts-row">
                {/* Gráfico 1 - Comparativo de Tributos */}
                <div className="reforma-chart-card">
                  <div className="chart-header">
                    <div className="chart-title">
                      <HiOutlineChartBar className="chart-icon" />
                      <span>Comparativo de Tributos</span>
                    </div>
                    <span className="chart-subtitle">
                      Sistema Atual vs IVA Dual
                    </span>
                  </div>
                  <div className="chart-container">
                    <ResponsiveContainer width="100%" height={380}>
                      <BarChart
                        data={comparativoTributosData}
                        margin={{ top: 5, right: 5, left: 35, bottom: 20 }}
                        barGap={2}
                        barCategoryGap="15%"
                      >
                        <defs>
                          <linearGradient
                            id="gradientAtual"
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1"
                          >
                            <stop offset="0%" stopColor="#9ca3af" stopOpacity={1} />
                            <stop
                              offset="100%"
                              stopColor="#6b7280"
                              stopOpacity={1}
                            />
                          </linearGradient>
                          <linearGradient
                            id="gradientNovo"
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1"
                          >
                            <stop offset="0%" stopColor="#60a5fa" stopOpacity={1} />
                            <stop
                              offset="100%"
                              stopColor="#3b82f6"
                              stopOpacity={1}
                            />
                          </linearGradient>
                        </defs>
                        <CartesianGrid
                          strokeDasharray="3 3"
                          stroke={theme.border}
                          vertical={false}
                        />
                        <XAxis
                          dataKey="name"
                          tick={{
                            fill: theme.text,
                            fontSize: 11,
                            fontWeight: 500,
                            opacity: 0.7,
                          }}
                          axisLine={false}
                          tickLine={false}
                          dy={5}
                        />
                        <YAxis
                          tick={{ fill: theme.text, fontSize: 10, opacity: 0.6 }}
                          axisLine={false}
                          tickLine={false}
                          tickFormatter={(value) =>
                            value >= 1000000
                              ? `${(value / 1000000).toFixed(1)}M`
                              : value >= 1000
                              ? `${(value / 1000).toFixed(0)}K`
                              : value
                          }
                          width={40}
                        />
                        <Tooltip
                          cursor={{ fill: theme.tableRowHover }}
                          contentStyle={{
                            background: theme.card,
                            border: `1px solid ${theme.border}`,
                            borderRadius: "8px",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                            padding: "8px 12px",
                          }}
                          formatter={(value, name) => [
                            `R$ ${value.toLocaleString("pt-BR")}`,
                            name === "atual" ? "Sistema Atual" : "IVA Dual",
                          ]}
                          labelStyle={{
                            color: theme.text,
                            fontWeight: 600,
                            marginBottom: 4,
                          }}
                          itemStyle={{
                            color: theme.text,
                            opacity: 0.8,
                            fontSize: 12,
                          }}
                        />
                        <Bar
                          dataKey="atual"
                          name="atual"
                          fill="url(#gradientAtual)"
                          radius={[4, 4, 0, 0]}
                          maxBarSize={50}
                        />
                        <Bar
                          dataKey="novo"
                          name="novo"
                          fill="url(#gradientNovo)"
                          radius={[4, 4, 0, 0]}
                          maxBarSize={50}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </Container>
  );
}
